import { motion } from 'framer-motion';
import { CheckCircle2, type LucideIcon } from 'lucide-react';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import { TAB_WEIGHTS, type TabId } from '../progress';

export type TabDef = { id: TabId; number: number; label: string; hint: string; icon: LucideIcon };

/**
 * Weighted tab boxes — each box's width is its share of the form's data,
 * and its own completion renders as a light green fill inside the box.
 */
export default function WeightedProgress({
  tabs,
  completion,
  activeId,
  errorTab,
  onJump,
}: {
  tabs: TabDef[];
  completion: Record<TabId, number>;
  activeId: TabId;
  errorTab: TabId | null;
  onJump: (id: TabId) => void;
}) {
  return (
    <div className="flex items-stretch gap-1.5 pb-3 pt-1">
      {tabs.map((t) => {
        const weight = TAB_WEIGHTS[t.id];
        const frac = Math.min(1, Math.max(0, completion[t.id]));
        const active = t.id === activeId;
        const done = frac >= 1;
        const hasError = errorTab === t.id;
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onJump(t.id)}
            title={`${t.label} — ${Math.round(frac * 100)}% complete (${weight}% of form)`}
            className="relative flex min-w-0 items-center gap-1.5 overflow-hidden rounded-xl px-2.5 py-2 text-[11px] font-bold transition"
            style={{
              width: `${weight}%`,
              background: SOVEREIGN.inputBg,
              border: active ? '1px solid rgba(11,59,46,0.5)' : '1px solid rgba(11,59,46,0.18)',
              color: active ? SOVEREIGN.title : done ? SOVEREIGN.bottle : SOVEREIGN.kpiCaption,
              boxShadow: active
                ? 'inset 0 -2px 0 0 #C9A961, 0 1px 3px rgba(42,31,23,0.12)'
                : 'inset 0 1px 0 rgba(255,255,255,0.5)',
            }}
          >
            {/* light progress fill as the box background */}
            <motion.span
              aria-hidden
              className="absolute inset-y-0 left-0"
              initial={false}
              animate={{ width: `${frac * 100}%` }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background: done
                  ? 'linear-gradient(90deg, rgba(51,146,116,0.22), rgba(31,90,71,0.18))'
                  : 'linear-gradient(90deg, rgba(51,146,116,0.18), rgba(51,146,116,0.10))',
                borderRight: frac > 0 && frac < 1 ? '1px solid rgba(51,146,116,0.35)' : 'none',
              }}
            />
            <span
              className="relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold"
              style={
                hasError
                  ? { background: 'rgba(159,18,57,0.14)', color: '#9F1239', border: '1px solid #9F1239' }
                  : done
                    ? { background: 'rgba(51,146,116,0.2)', color: SOVEREIGN.bottle }
                    : active
                      ? { background: SOVEREIGN.btn, color: '#fff' }
                      : { background: 'rgba(11,59,46,0.08)', color: SOVEREIGN.kpiCaption }
              }
            >
              {hasError ? '!' : done ? <CheckCircle2 className="h-3 w-3" strokeWidth={3} /> : t.number}
            </span>
            <span className="relative hidden truncate lg:inline">{t.label}</span>
            <Icon className="relative h-3.5 w-3.5 shrink-0 lg:hidden" />
          </button>
        );
      })}
    </div>
  );
}
